/* ============================================================================
   COLLECTION GALLERY
   Desktop: vertical pin-scroll drives horizontal painting morph.
   Mobile: native horizontal swipe on transparent track drives the morph.
   Jul 13: onResize now ignores height-only resize events on mobile (iOS
   Safari fires resize when the toolbar collapses during vertical scroll,
   which was rebuilding the placard innerHTML mid-scroll — visible flicker
   and position jump). Width/orientation changes still re-layout fully.
   ============================================================================ */

(function() {
  'use strict';

  var cg, pin, stage, main, textInner, textSection, titleBlockEl;
  var slides = [];
  var slideMetadata = [];
  var slideLastTransform = [];
  var productCount = 0;

  var rafId = null;
  var hasBeenRead = [];
  var renderedTextIndex = -1;
  var currentWordSpans = [];
  var currentDetailsEl = null;
  var headerOffset = 0;

  var residentPaintingIndex = -1;
  var enteredAsRead = false;

  var animScrollRafId = null;
  var animScrollStartY = 0;
  var animScrollTargetY = 0;
  var animScrollStartTime = 0;
  var animScrollDuration = 1200;

  /* iOS toolbar guard: last viewport width seen by onResize */
  var lastResizeWidth = window.innerWidth;

  var READING_END = 0.65;
  var DETAILS_REVEAL_START = 0.85;
  var THUMB_OFFSET_VW = 38;
  var THUMB_SCALE = 0.18;
  var THUMB_BASE_OPACITY = 0.25;
  var WORD_FADED_OPACITY = 0.18;
  var WORD_FADE_WINDOW_FACTOR = 1.5;
  var STAGE_WIDTH_FRACTION = 0.62;
  var STAGE_MAX_WIDTH = 1000;
  var STORY_CHAR_LIMIT = 125;
  var CTA_LABEL = 'View Painting';

  /* Gallery story overrides — shortened first lines for specific paintings */
  var STORY_OVERRIDES = [
    { match: 'Quiet Work', text: 'Riders hold the outer edges\u2026' },
    { match: 'At Hand', text: 'A work carried by hardened hands\u2026' },
    { match: 'Last Light', text: 'Long shadows gather\u2026' }
  ];

  var nextLinkEl = null;

  /* Chevrons — same 8x12 mark as the PDP room gallery arrows */
  var ARROW_PREV_SVG = '<svg viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7 1L2 6L7 11" stroke="currentColor" stroke-width="1.5"/></svg>';
  var ARROW_NEXT_SVG = '<svg viewBox="0 0 8 12" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 1L6 6L1 11" stroke="currentColor" stroke-width="1.5"/></svg>';

  // Mobile portrait
  var MOBILE_PORTRAIT_THUMB_OFFSET_VW = 50;
  var MOBILE_PORTRAIT_THUMB_SCALE = 0.16;
  var MOBILE_PORTRAIT_STAGE_WIDTH_FRACTION = 0.92;

  // Mobile landscape
  var MOBILE_LANDSCAPE_THUMB_OFFSET_VW = 50;
  var MOBILE_LANDSCAPE_THUMB_SCALE = 0.18;
  var MOBILE_LANDSCAPE_STAGE_WIDTH_FRACTION = 0.55;

  // Mobile-specific state
  var cgmTrack = null;
  var cgmTrackRafId = null;
  var cgmInited = false;

  // Tap-vs-swipe detection thresholds
  var TAP_THRESHOLD_PX = 10;
  var TAP_THRESHOLD_MS = 300;
  var touchStartX = 0;
  var touchStartY = 0;
  var touchStartTime = 0;

  function isMobile() {
    return window.innerWidth <= 900;
  }
  function isLandscape() {
    return window.innerWidth > window.innerHeight;
  }
  function isMobileLandscape() {
    return isMobile() && isLandscape();
  }
  function isMobilePortrait() {
    return isMobile() && !isLandscape();
  }

  function init() {
    cg = document.getElementById('cg');
    if (!cg) return;

    pin = document.getElementById('cgPin');
    stage = document.getElementById('cgStage');
    main = document.getElementById('cgMain');
    textInner = document.getElementById('cgTextInner');
    textSection = document.getElementById('cgTextSection');
    cgmTrack = document.getElementById('cgmTrack');
    titleBlockEl = cg.querySelector('.cg__title-block');

    if (!pin || !stage || !main || !textInner || !textSection) return;

    slides = Array.from(main.querySelectorAll('.cg__slide'));
    productCount = slides.length;
    if (productCount === 0) return;

    slideMetadata = slides.map(function(slide) {
      var w = parseFloat(slide.dataset.imgWidth) || 1600;
      var h = parseFloat(slide.dataset.imgHeight) || 1200;
      return { width: w, height: h, aspectRatio: w / h };
    });

    slideLastTransform = new Array(productCount).fill('');

    slides.forEach(function(slide) {
      slide.style.setProperty('cursor', 'pointer', 'important');
      var img = slide.querySelector('img');
      if (img) {
        img.style.setProperty('cursor', 'pointer', 'important');
        img.style.setProperty('pointer-events', 'auto', 'important');
        img.setAttribute('draggable', 'false');
      }
    });

    hasBeenRead = new Array(productCount).fill(false);
    hasBeenRead[0] = true; /* Part 1 opens fully revealed */
    residentPaintingIndex = 0;
    enteredAsRead = true;

    /* Next step link — hangs where the ninth painting would */
    nextLinkEl = document.createElement('a');
    nextLinkEl.className = 'cg__next-link';
    nextLinkEl.href = '/collections/due-west';
    nextLinkEl.textContent = 'VIEW THE INSPIRATION';
    main.appendChild(nextLinkEl);

    headerOffset = detectHeaderOffset();
    cg.style.setProperty('--cg-header-offset', headerOffset + 'px');

    renderTextForSlide(0);
    alignTextForSlide(0);
    positionTitle();
    renderProgress(0, 0);
    applyTextStateForPainting(0, 0, 1, 0);
    updateLandscapeTitleOpacity(0);

    bindEvents();

    var firstImg = slides[0].querySelector('img');
    if (firstImg && !firstImg.complete) {
      firstImg.addEventListener('load', function() {
        textSection.classList.add('is-visible');
        alignTextForSlide(renderedTextIndex);
        positionTitle();
      });
    } else {
      textSection.classList.add('is-visible');
    }

    // Desktop click-to-jump
    slides.forEach(function(slide) {
      slide.addEventListener('click', function(e) {
        if (isMobile()) return; // mobile uses tap-on-track
        var idx = parseInt(slide.dataset.index, 10);
        var activeIdx = getActivePaintingIndex();
        if (idx === activeIdx) return;
        e.preventDefault();
        jumpToSlide(idx);
      });
    });

    // Mobile-specific init
    if (isMobile()) {
      initMobileTrack();
    }
  }

  function initMobileTrack() {
    if (cgmInited) return;
    if (!cgmTrack) return;
    cgmInited = true;

    // Track scroll listener: drives painting morph based on scrollLeft
    cgmTrack.addEventListener('scroll', onMobileTrackScrollThrottled, { passive: true });

    // Tap-vs-swipe detection on the track itself.
    cgmTrack.addEventListener('touchstart', function(e) {
      if (e.touches.length !== 1) return;
      touchStartX = e.touches[0].clientX;
      touchStartY = e.touches[0].clientY;
      touchStartTime = Date.now();
    }, { passive: true });

    cgmTrack.addEventListener('touchend', function(e) {
      if (e.changedTouches.length !== 1) return;
      var dx = Math.abs(e.changedTouches[0].clientX - touchStartX);
      var dy = Math.abs(e.changedTouches[0].clientY - touchStartY);
      var dt = Date.now() - touchStartTime;
      if (dx < TAP_THRESHOLD_PX && dy < TAP_THRESHOLD_PX && dt < TAP_THRESHOLD_MS) {
        var centreIdx = getMobileActivePaintingIndex();
        if (centreIdx >= productCount) {
          window.location.href = '/collections/due-west';
          return;
        }
        var slide = slides[centreIdx];
        if (slide && slide.dataset.productUrl) {
          window.location.href = slide.dataset.productUrl;
        }
      }
    }, { passive: true });

    // Click fallback for non-touch devices.
    cgmTrack.addEventListener('click', function(e) {
      if (e.detail === 0) return; // synthesised click, ignore
      var centreIdx = getMobileActivePaintingIndex();
      if (centreIdx >= productCount) {
        window.location.href = '/collections/due-west';
        return;
      }
      var slide = slides[centreIdx];
      if (slide && slide.dataset.productUrl) {
        window.location.href = slide.dataset.productUrl;
      }
    });

    // Swipe anywhere above the View Painting button steps the gallery.
    // The image track scrolls natively; arrows and the CTA keep their taps.
    var pinSwipeX = 0;
    var pinSwipeY = 0;
    var pinSwipeValid = false;

    pin.addEventListener('touchstart', function(e) {
      pinSwipeValid = false;
      if (!isMobile()) return;
      if (e.touches.length !== 1) return;
      var t = e.target;
      if (cgmTrack && cgmTrack.contains(t)) return;
      if (t.closest && (t.closest('.cgm-arrows') || t.closest('.cg__cta-btn'))) return;
      pinSwipeX = e.touches[0].clientX;
      pinSwipeY = e.touches[0].clientY;
      pinSwipeValid = true;
    }, { passive: true });

    pin.addEventListener('touchend', function(e) {
      if (!pinSwipeValid) return;
      pinSwipeValid = false;
      if (e.changedTouches.length !== 1) return;
      var dx = e.changedTouches[0].clientX - pinSwipeX;
      var dy = e.changedTouches[0].clientY - pinSwipeY;
      if (Math.abs(dx) < 40 || Math.abs(dx) <= Math.abs(dy)) return;
      mobileStep(dx < 0 ? 1 : -1);
    }, { passive: true });

    // Set initial position to slide 0
    requestAnimationFrame(function() {
      cgmTrack.scrollLeft = 0;
      onMobileTrackScroll();
    });
  }

  function onMobileTrackScrollThrottled() {
    if (cgmTrackRafId) return;
    cgmTrackRafId = requestAnimationFrame(function() {
      onMobileTrackScroll();
      cgmTrackRafId = null;
    });
  }

  function onMobileTrackScroll() {
    if (!cgmTrack) return;
    var scrollLeft = cgmTrack.scrollLeft;
    var trackWidth = cgmTrack.clientWidth;
    if (trackWidth === 0) return;

    var maxIndex = Math.max(productCount - 1, cgmTrack.children.length - 1);
    var globalIndex = scrollLeft / trackWidth;
    globalIndex = Math.max(0, Math.min(maxIndex, globalIndex));

    var paintingIndex = Math.min(Math.floor(globalIndex), productCount - 1);
    var localProgress = globalIndex - paintingIndex;
    var paintingTransition = localProgress;

    renderProgress(globalIndex, paintingTransition);
    updateLandscapeTitleOpacity(globalIndex);

    var textPaintingIndex;
    if (paintingTransition < 0.5) {
      textPaintingIndex = paintingIndex;
    } else {
      textPaintingIndex = Math.min(paintingIndex + 1, productCount - 1);
    }

    if (textPaintingIndex !== renderedTextIndex) {
      renderTextForSlide(textPaintingIndex);
    }

    if (globalIndex > productCount - 1) {
      /* Beyond the last painting: fade the placard out toward the inspiration step */
      var overshoot = globalIndex - (productCount - 1);
      if (textInner) textInner.style.opacity = String(Math.max(0, 1 - overshoot * 2));
    } else {
      applyMobileTextState(paintingTransition);
    }
  }

  function getMobileActivePaintingIndex() {
    if (!cgmTrack) return 0;
    var scrollLeft = cgmTrack.scrollLeft;
    var trackWidth = cgmTrack.clientWidth;
    if (trackWidth === 0) return 0;
    return Math.round(scrollLeft / trackWidth);
  }

  function applyMobileTextState(paintingTransition) {
    var sectionOpacity;
    if (paintingTransition < 0.5) {
      sectionOpacity = 1 - (paintingTransition / 0.5);
    } else {
      sectionOpacity = (paintingTransition - 0.5) / 0.5;
    }
    sectionOpacity = Math.max(0, Math.min(1, sectionOpacity));
    if (textInner) textInner.style.opacity = sectionOpacity;
  }

  // Landscape-only: fade collection title as user swipes past first painting.
  // Fully visible at painting 0, fully hidden by painting 1.
  // No-op when no title block exists (breadcrumb layout) and on desktop/portrait.
  function updateLandscapeTitleOpacity(globalIndex) {
    if (!titleBlockEl) return;
    if (isMobileLandscape()) {
      var titleOpacity = Math.max(0, Math.min(1, 1 - globalIndex));
      titleBlockEl.style.opacity = titleOpacity;
    } else {
      titleBlockEl.style.opacity = '';
    }
  }

  function detectHeaderOffset() {
    var selectors = [
      'header[role="banner"]',
      '.shopify-section-group-header-group',
      '.shopify-section--header',
      '.header__wrapper',
      'header.header',
      'header'
    ];
    for (var i = 0; i < selectors.length; i++) {
      var el = document.querySelector(selectors[i]);
      if (!el) continue;
      var style = window.getComputedStyle(el);
      if (style.position === 'fixed' || style.position === 'sticky') {
        var rect = el.getBoundingClientRect();
        if (rect.height > 0) return rect.height;
      }
    }
    return 0;
  }

  function bindEvents() {
    window.addEventListener('scroll', onScrollThrottled, { passive: true });
    window.addEventListener('resize', onResize);
    window.addEventListener('orientationchange', onResize);

    window.addEventListener('keydown', function(e) {
      if (isMobile()) return;
      if (e.key === 'ArrowRight') jumpToSlide(getActivePaintingIndex() + 1);
      if (e.key === 'ArrowLeft') jumpToSlide(getActivePaintingIndex() - 1);
    });

    window.addEventListener('wheel', cancelAnimatedScroll, { passive: true });
    window.addEventListener('touchstart', cancelAnimatedScroll, { passive: true });

    slides.forEach(function(slide, idx) {
      var img = slide.querySelector('img');
      if (img && !img.complete) {
        img.addEventListener('load', function() {
          if (idx === renderedTextIndex) alignTextForSlide(idx);
        });
      }
    });
  }

  function onResize() {
    /* iOS Safari fires resize when the toolbar collapses/expands during
       vertical scroll — height changes, width does not. Re-laying out on
       those events rebuilt the placard mid-scroll (flicker + jump).
       On mobile, only re-layout when the width actually changes
       (rotation, split view, breakpoint crossing). Desktop always runs. */
    var w = window.innerWidth;
    if (isMobile() && w === lastResizeWidth) return;
    lastResizeWidth = w;

    headerOffset = detectHeaderOffset();
    cg.style.setProperty('--cg-header-offset', headerOffset + 'px');
    positionTitle();
    if (renderedTextIndex >= 0) {
      renderTextForSlide(renderedTextIndex);
      alignTextForSlide(renderedTextIndex);
    }
    if (isMobile()) {
      if (!cgmInited) initMobileTrack();
      onMobileTrackScroll();
    } else {
      onScroll();
    }
    // Re-evaluate landscape title visibility on orientation change
    if (titleBlockEl) {
      if (isMobileLandscape()) {
        var idx = cgmTrack && cgmTrack.clientWidth ? cgmTrack.scrollLeft / cgmTrack.clientWidth : 0;
        updateLandscapeTitleOpacity(idx);
      } else {
        titleBlockEl.style.opacity = '';
      }
    }
  }

  function onScrollThrottled() {
    if (isMobile()) return;
    if (rafId) return;
    rafId = requestAnimationFrame(function() {
      onScroll();
      rafId = null;
    });
  }

  function onScroll() {
    if (isMobile()) return;
    var rect = cg.getBoundingClientRect();
    var totalScrollableDistance = cg.offsetHeight - window.innerHeight;
    if (totalScrollableDistance <= 0) return;

    var scrolled = Math.max(0, Math.min(1, -rect.top / totalScrollableDistance));

    var paintingFloat = scrolled * productCount;
    var paintingIndex = Math.min(Math.floor(paintingFloat), productCount - 1);
    var localProgress = paintingFloat - paintingIndex;

    if (paintingIndex === productCount - 1) {
      localProgress = Math.min(localProgress, READING_END);
    }

    if (paintingIndex !== residentPaintingIndex) {
      residentPaintingIndex = paintingIndex;
      enteredAsRead = hasBeenRead[paintingIndex];
    }

    var textRevealProgress, paintingTransition, globalIndex;

    if (enteredAsRead) {
      textRevealProgress = 1;
      paintingTransition = localProgress;
      globalIndex = paintingIndex + paintingTransition;
    } else if (localProgress <= READING_END) {
      textRevealProgress = localProgress / READING_END;
      paintingTransition = 0;
      globalIndex = paintingIndex;
    } else {
      textRevealProgress = 1;
      paintingTransition = (localProgress - READING_END) / (1 - READING_END);
      globalIndex = paintingIndex + paintingTransition;
    }

    for (var i = 0; i < paintingIndex; i++) hasBeenRead[i] = true;
    if (textRevealProgress >= 1) hasBeenRead[paintingIndex] = true;

    renderProgress(globalIndex, paintingTransition);

    var textPaintingIndex;
    if (paintingTransition < 0.5) {
      textPaintingIndex = paintingIndex;
    } else {
      textPaintingIndex = Math.min(paintingIndex + 1, productCount - 1);
    }

    if (textPaintingIndex !== renderedTextIndex) {
      renderTextForSlide(textPaintingIndex);
      alignTextForSlide(textPaintingIndex);
    }

    applyTextStateForPainting(textPaintingIndex, paintingIndex, textRevealProgress, paintingTransition);
  }

  function renderProgress(globalIndex, paintingTransition) {
    var stageRect = stage.getBoundingClientRect();
    var stageWidth = stageRect.width;
    var stageHeight = stageRect.height;

    var widthFraction, thumbOffset, thumbScale;
    if (isMobilePortrait()) {
      widthFraction = MOBILE_PORTRAIT_STAGE_WIDTH_FRACTION;
      thumbOffset = MOBILE_PORTRAIT_THUMB_OFFSET_VW;
      thumbScale = MOBILE_PORTRAIT_THUMB_SCALE;
    } else if (isMobileLandscape()) {
      widthFraction = MOBILE_LANDSCAPE_STAGE_WIDTH_FRACTION;
      thumbOffset = MOBILE_LANDSCAPE_THUMB_OFFSET_VW;
      thumbScale = MOBILE_LANDSCAPE_THUMB_SCALE;
    } else {
      widthFraction = STAGE_WIDTH_FRACTION;
      thumbOffset = THUMB_OFFSET_VW;
      thumbScale = THUMB_SCALE;
    }

    var maxCentreWidth;
    if (isMobile()) {
      maxCentreWidth = stageWidth * widthFraction;
    } else {
      maxCentreWidth = Math.min(stageWidth * widthFraction, STAGE_MAX_WIDTH);
    }

    slides.forEach(function(slide, i) {
      var meta = slideMetadata[i];
      var distance = i - globalIndex;
      var absDistance = Math.abs(distance);
      var sign = distance < 0 ? -1 : 1;

      if (absDistance >= 2) {
        slide.classList.remove('cg__slide--visible');
        slide.classList.remove('cg__slide--clickable');
        if (slide.style.opacity !== '0') slide.style.opacity = '0';
        return;
      }

      var slideAR = meta.aspectRatio;
      var centreWidth, centreHeight;
      if (slideAR >= 1) {
        centreWidth = maxCentreWidth;
        centreHeight = centreWidth / slideAR;
        if (centreHeight > stageHeight) {
          centreHeight = stageHeight;
          centreWidth = centreHeight * slideAR;
        }
      } else {
        centreHeight = stageHeight;
        centreWidth = centreHeight * slideAR;
        if (centreWidth > maxCentreWidth) {
          centreWidth = maxCentreWidth;
          centreHeight = centreWidth / slideAR;
        }
      }

      var widthPx = centreWidth + 'px';
      var heightPx = centreHeight + 'px';
      if (slide.style.width !== widthPx) slide.style.width = widthPx;
      if (slide.style.height !== heightPx) slide.style.height = heightPx;

      slide.classList.add('cg__slide--visible');

      var translateVw, scale, opacity;

      if (absDistance <= 1) {
        translateVw = distance * thumbOffset;
        scale = 1 - absDistance * (1 - thumbScale);
        opacity = 1 - absDistance * (1 - THUMB_BASE_OPACITY);
      } else {
        var t = absDistance - 1;
        translateVw = sign * (thumbOffset + t * (thumbOffset * 0.6));
        scale = thumbScale * (1 - t * 0.5);
        opacity = THUMB_BASE_OPACITY * (1 - t);
      }

      var translateVwRounded = Math.round(translateVw * 1000) / 1000;
      var scaleRounded = Math.round(scale * 1000) / 1000;
      var opacityRounded = Math.round(opacity * 1000) / 1000;

      var ty = isMobilePortrait() ? '0' : '-50%';
      var newTransform = 'translate(-50%, ' + ty + ') translateX(' +
        translateVwRounded + 'vw) scale(' + scaleRounded + ')';
      var newOpacity = String(opacityRounded);
      var newZIndex = absDistance < 0.5 ? '3' : '2';

      if (slideLastTransform[i] !== newTransform) {
        slide.style.transform = newTransform;
        slideLastTransform[i] = newTransform;
      }
      if (slide.style.opacity !== newOpacity) slide.style.opacity = newOpacity;
      if (slide.style.zIndex !== newZIndex) slide.style.zIndex = newZIndex;

      slide.classList.add('cg__slide--clickable');
    });

    if (nextLinkEl) {
      var nd = productCount - globalIndex;
      var nop = Math.max(0, Math.min(1, 2 - nd));
      var ntx = Math.round(nd * thumbOffset * 1000) / 1000;
      nextLinkEl.style.transform = 'translate(-50%, -50%) translateX(' + ntx + 'vw)';
      nextLinkEl.style.opacity = String(Math.round(nop * 1000) / 1000);
      nextLinkEl.style.pointerEvents = (!isMobile() && nop > 0.5) ? 'auto' : 'none';
    }

    if (isMobilePortrait()) {
      positionMobilePortraitText(globalIndex);
    }
  }

  function positionMobilePortraitText(globalIndex) {
    if (!cg) return;
    var centreIdx = Math.round(globalIndex);
    if (centreIdx < 0 || centreIdx >= slides.length) return;
    var meta = slideMetadata[centreIdx];
    if (!meta) return;

    var stageRect = stage.getBoundingClientRect();
    var pinRect = pin.getBoundingClientRect();
    var stageWidth = stageRect.width;
    var stageHeight = stageRect.height;
    var maxCentreWidth = stageWidth * MOBILE_PORTRAIT_STAGE_WIDTH_FRACTION;
    var ar = meta.aspectRatio;

    var centreWidth, centreHeight;
    if (ar >= 1) {
      centreWidth = maxCentreWidth;
      centreHeight = centreWidth / ar;
      if (centreHeight > stageHeight) {
        centreHeight = stageHeight;
        centreWidth = centreHeight * ar;
      }
    } else {
      centreHeight = stageHeight;
      centreWidth = centreHeight * ar;
      if (centreWidth > maxCentreWidth) {
        centreWidth = maxCentreWidth;
        centreHeight = centreWidth / ar;
      }
    }

    var imgLeftFromStage = (stageWidth - centreWidth) / 2;
    cg.style.setProperty('--cg-text-anchor-left', Math.max(8, imgLeftFromStage) + 'px');

    var imgRightFromStage = (stageWidth + centreWidth) / 2;
    var rightPaddingFromPin = pinRect.width - imgRightFromStage;
    cg.style.setProperty('--cg-text-anchor-right', Math.max(8, rightPaddingFromPin) + 'px');

    /* Paintings hang from the top of the stage, so the empty band below
       equals stageHeight - paintingHeight; pull the placard up over it */
    var pullM = Math.max(0, stageHeight - centreHeight);
    cg.style.setProperty('--cg-text-pull-m', pullM + 'px');
  }

  function mobileStep(dir) {
    if (!cgmTrack) return;
    var maxCell = cgmTrack.children.length - 1;
    var idx = Math.max(0, Math.min(maxCell, getMobileActivePaintingIndex() + dir));
    var w = cgmTrack.clientWidth;
    if (cgmTrack.scrollTo) {
      cgmTrack.scrollTo({ left: idx * w, behavior: 'smooth' });
    } else {
      cgmTrack.scrollLeft = idx * w;
    }
  }

  function truncateDescription(rawLines, charLimit) {
    var fullText = rawLines.join(' ');
    if (fullText.length <= charLimit) {
      return { lines: rawLines, truncated: false };
    }

    var truncated = fullText.substring(0, charLimit);
    var lastSpace = truncated.lastIndexOf(' ');
    if (lastSpace > 0) {
      truncated = truncated.substring(0, lastSpace);
    }
    truncated = truncated.replace(/[.,;:!?]+$/, '') + '…';

    var assembledLines = [];
    var consumedChars = 0;
    var truncatedNoEllipsis = truncated.replace('…', '');

    for (var i = 0; i < rawLines.length; i++) {
      var line = rawLines[i];
      var lineLength = line.length + (i < rawLines.length - 1 ? 1 : 0);
      if (consumedChars + line.length <= truncatedNoEllipsis.length) {
        assembledLines.push(line);
        consumedChars += lineLength;
      } else {
        var remaining = truncatedNoEllipsis.length - consumedChars;
        if (remaining > 0) {
          var partialLine = line.substring(0, remaining).trim();
          partialLine = partialLine.replace(/[.,;:!?]+$/, '');
          assembledLines.push(partialLine + '…');
        } else if (assembledLines.length > 0) {
          var last = assembledLines[assembledLines.length - 1];
          last = last.replace(/[.,;:!?]+$/, '');
          assembledLines[assembledLines.length - 1] = last + '…';
        }
        break;
      }
    }

    return { lines: assembledLines, truncated: true };
  }

  function renderTextForSlide(index) {
    var slide = slides[index];
    var part = slide.dataset.part;
    var title = slide.dataset.productTitle;
    var description = slide.dataset.productDescription || '';
    var smallestDims = slide.dataset.smallestPrintDims;
    var startingPrice = slide.dataset.startingPrice;
    var productUrl = slide.dataset.productUrl;

    var rawLines = description.split('|||')
      .map(function(s) { return s.trim(); })
      .filter(function(s) { return s.length > 0; });

    // First paragraph only (before the page break), always closed with an ellipsis.
    if (rawLines.length > 1) rawLines = rawLines.slice(0, 1);

    var storyLines;
    var override = null;
    for (var oi = 0; oi < STORY_OVERRIDES.length; oi++) {
      if (title && title.indexOf(STORY_OVERRIDES[oi].match) !== -1) {
        override = STORY_OVERRIDES[oi].text;
        break;
      }
    }
    if (override) {
      storyLines = [override];
    } else {
      var truncResult = truncateDescription(rawLines, STORY_CHAR_LIMIT);
      if (!truncResult.truncated && truncResult.lines.length > 0) {
        var lastIdx = truncResult.lines.length - 1;
        var lastLine = truncResult.lines[lastIdx].replace(/[.,;:!?…]+$/, '');
        truncResult.lines[lastIdx] = lastLine + '…';
      }
      storyLines = truncResult.lines;
    }

    var storyHTML = storyLines.map(function(line) {
      return '<p class="cg__story-line">' + line + '</p>';
    }).join('');

    var leftHTML = '<p class="cg__part-label">PART ' + part + '</p>' +
                   '<div class="cg__story">' + storyHTML + '</div>';

    var metaLines = ['OIL ON CANVAS', 'PRINT EDITIONS'];
    if (smallestDims) {
      metaLines.push('FROM ' + smallestDims.toUpperCase());
    }

    var detailsHTML =
      '<p class="cg__details-part">PART ' + part + '</p>' +
      '<p class="cg__product-title">' + title + '</p>' +
      '<p class="cg__product-meta">' + metaLines.join('<br>') + '</p>' +
      (startingPrice ? '<p class="cg__product-price">STARTING AT $' + startingPrice + '</p>' : '') +
      '<a class="cg__cta-btn" href="' + productUrl + '">' + CTA_LABEL + '</a>';

    textInner.innerHTML =
      '<div class="cg__text-left">' + leftHTML + '</div>' +
      '<div class="cg__text-right"><div class="cg__details">' + detailsHTML + '</div></div>' +
      '<div class="cgm-arrows">' +
        '<button class="cgm-arrow cgm-arrow--prev" type="button" aria-label="Previous painting">' + ARROW_PREV_SVG + '</button>' +
        '<button class="cgm-arrow cgm-arrow--next" type="button" aria-label="Next painting">' + ARROW_NEXT_SVG + '</button>' +
      '</div>';

    var prevBtn = textInner.querySelector('.cgm-arrow--prev');
    var nextBtn = textInner.querySelector('.cgm-arrow--next');
    if (prevBtn) {
      if (index === 0) prevBtn.classList.add('cgm-arrow-disabled');
      prevBtn.addEventListener('click', function(e) { e.stopPropagation(); mobileStep(-1); });
    }
    if (nextBtn) {
      if (index === productCount - 1) nextBtn.classList.add('cgm-arrow-disabled');
      nextBtn.addEventListener('click', function(e) { e.stopPropagation(); mobileStep(1); });
    }

    var storyEl = textInner.querySelector('.cg__story');
    currentWordSpans = wrapWords(storyEl);
    currentDetailsEl = textInner.querySelector('.cg__details');

    var detailsPartEl = textInner.querySelector('.cg__details-part');
    if (detailsPartEl) {
      detailsPartEl.style.display = isMobile() ? 'block' : 'none';
    }

    var titleEl = textInner.querySelector('.cg__product-title');
    if (titleEl && !isMobile()) {
      titleEl.textContent = title.toUpperCase();
    }

    renderedTextIndex = index;

    if (isMobileLandscape()) {
      renderLandscapeDetails(index, title, part, metaLines, startingPrice, productUrl);
    } else {
      slides.forEach(function(s) {
        var ld = s.querySelector('.cgm-landscape-details');
        if (ld) ld.remove();
      });
    }
  }

  function renderLandscapeDetails(index, title, part, metaLines, startingPrice, productUrl) {
    slides.forEach(function(s) {
      var existing = s.querySelector('.cgm-landscape-details');
      if (existing) existing.remove();
    });

    var slide = slides[index];
    if (!slide) return;

    var detailsHTML =
      '<div class="cgm-landscape-details">' +
      '<p class="cg__details-part">PART ' + part + '</p>' +
      '<p class="cg__product-title">' + title + '</p>' +
      '<p class="cg__product-meta">' + metaLines.join('<br>') + '</p>' +
      (startingPrice ? '<p class="cg__product-price">STARTING AT $' + startingPrice + '</p>' : '') +
      '</div>';

    slide.insertAdjacentHTML('beforeend', detailsHTML);

    var ld = slide.querySelector('.cgm-landscape-details');
    if (ld) {
      ld.style.cssText =
        'position: absolute;' +
        'left: 100%;' +
        'top: 50%;' +
        'transform: translateY(-50%);' +
        'margin-left: 24px;' +
        'width: 40vw;' +
        'max-width: 280px;' +
        'text-align: left;' +
        'pointer-events: none;';

      var partEl = ld.querySelector('.cg__details-part');
      if (partEl) {
        partEl.style.cssText =
          'font-family: var(--cg-font-sans) !important;' +
          'font-size: 7pt !important;' +
          'letter-spacing: 0.1em !important;' +
          'text-transform: uppercase !important;' +
          'color: var(--cg-color-text-light) !important;' +
          'margin: 0 0 6px 0 !important;';
      }

      var titleEl = ld.querySelector('.cg__product-title');
      if (titleEl) {
        titleEl.style.cssText =
          'font-family: var(--cg-font-serif) !important;' +
          'font-size: 14pt !important;' +
          'font-weight: 400 !important;' +
          'line-height: 1.2 !important;' +
          'color: var(--cg-color-text) !important;' +
          'margin: 0 0 8px 0 !important;' +
          'text-transform: none !important;' +
          'white-space: nowrap;';
      }

      var metaEl = ld.querySelector('.cg__product-meta');
      if (metaEl) {
        metaEl.style.cssText =
          'font-family: var(--cg-font-sans) !important;' +
          'font-size: 7pt !important;' +
          'letter-spacing: 0.1em !important;' +
          'text-transform: uppercase !important;' +
          'line-height: 1.7 !important;' +
          'color: var(--cg-color-text-light) !important;' +
          'margin: 0 0 6px 0 !important;';
      }

      var priceEl = ld.querySelector('.cg__product-price');
      if (priceEl) {
        priceEl.style.cssText =
          'font-family: var(--cg-font-sans) !important;' +
          'font-size: 7pt !important;' +
          'letter-spacing: 0.1em !important;' +
          'text-transform: uppercase !important;' +
          'line-height: 1.7 !important;' +
          'color: var(--cg-color-text-light) !important;' +
          'margin: 0 !important;';
      }
    }
  }

  function wrapWords(rootElement) {
    if (!rootElement) return [];
    var walker = document.createTreeWalker(rootElement, NodeFilter.SHOW_TEXT, null, false);
    var textNodes = [];
    var node;
    while ((node = walker.nextNode())) {
      if (node.parentNode && node.parentNode.classList && node.parentNode.classList.contains('cg__word')) continue;
      textNodes.push(node);
    }
    textNodes.forEach(function(textNode) {
      var text = textNode.textContent;
      if (!text || /^\s*$/.test(text)) return;
      var parent = textNode.parentNode;
      var fragment = document.createDocumentFragment();
      var parts = text.split(/(\s+)/);
      parts.forEach(function(part) {
        if (part.length === 0) return;
        if (/^\s+$/.test(part)) {
          fragment.appendChild(document.createTextNode(part));
        } else {
          var span = document.createElement('span');
          span.className = 'cg__word';
          span.textContent = part;
          fragment.appendChild(span);
        }
      });
      parent.replaceChild(fragment, textNode);
    });
    return Array.from(rootElement.querySelectorAll('.cg__word'));
  }

  function applyTextStateForPainting(textPaintingIndex, currentPaintingIndex, textRevealProgress, paintingTransition) {
    if (isMobile()) {
      setDetailsOpacity(1);
      return;
    }

    var sectionOpacity;
    if (paintingTransition === 0) {
      sectionOpacity = 1;
    } else {
      var fadeWindow = 0.15;
      if (paintingTransition < fadeWindow) {
        sectionOpacity = 1 - (paintingTransition / fadeWindow);
      } else if (paintingTransition > 1 - fadeWindow) {
        sectionOpacity = 1 - ((1 - paintingTransition) / fadeWindow);
      } else {
        sectionOpacity = 0;
      }
    }
    textInner.style.opacity = sectionOpacity;

    if (hasBeenRead[textPaintingIndex]) {
      setAllWordsRevealed();
      setDetailsOpacity(1);
      return;
    }

    if (textPaintingIndex === currentPaintingIndex && paintingTransition === 0) {
      updateWordOpacities(textRevealProgress);
      var detailsOpacity = Math.max(0, Math.min(1,
        (textRevealProgress - DETAILS_REVEAL_START) / (1 - DETAILS_REVEAL_START)
      ));
      setDetailsOpacity(detailsOpacity);
    } else {
      updateWordOpacities(0);
      setDetailsOpacity(0);
    }
  }

  function updateWordOpacities(revealProgress) {
    var total = currentWordSpans.length;
    if (total === 0) return;
    var fadeWindow = WORD_FADE_WINDOW_FACTOR / total;

    for (var i = 0; i < total; i++) {
      var fullAt = i / total;
      var trigger = fullAt - fadeWindow;
      var opacity;
      if (revealProgress >= fullAt) {
        opacity = 1;
      } else if (revealProgress <= trigger) {
        opacity = WORD_FADED_OPACITY;
      } else {
        var p = (revealProgress - trigger) / fadeWindow;
        opacity = WORD_FADED_OPACITY + (1 - WORD_FADED_OPACITY) * p;
      }
      currentWordSpans[i].style.opacity = opacity;
    }
  }

  function setAllWordsRevealed() {
    for (var i = 0; i < currentWordSpans.length; i++) {
      currentWordSpans[i].style.opacity = '1';
    }
  }

  function setDetailsOpacity(opacity) {
    if (currentDetailsEl) currentDetailsEl.style.opacity = opacity;
  }

  function getActivePaintingIndex() {
    return Math.max(0, Math.min(productCount - 1, renderedTextIndex));
  }

  function easeInOutCubic(t) {
    return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
  }

  function cancelAnimatedScroll() {
    if (animScrollRafId) {
      cancelAnimationFrame(animScrollRafId);
      animScrollRafId = null;
    }
  }

  function animateScrollTo(targetY, duration) {
    cancelAnimatedScroll();
    animScrollStartY = window.pageYOffset;
    animScrollTargetY = targetY;
    animScrollStartTime = performance.now();
    animScrollDuration = duration || 1200;

    function step(now) {
      var elapsed = now - animScrollStartTime;
      var t = Math.min(1, elapsed / animScrollDuration);
      var eased = easeInOutCubic(t);
      var currentY = animScrollStartY + (animScrollTargetY - animScrollStartY) * eased;
      window.scrollTo(0, currentY);

      if (t < 1) {
        animScrollRafId = requestAnimationFrame(step);
      } else {
        animScrollRafId = null;
      }
    }

    animScrollRafId = requestAnimationFrame(step);
  }

  function jumpToSlide(targetIndex) {
    if (targetIndex < 0) targetIndex = 0;
    if (targetIndex > productCount - 1) targetIndex = productCount - 1;

    for (var i = 0; i <= targetIndex; i++) hasBeenRead[i] = true;

    var totalScrollableDistance = cg.offsetHeight - window.innerHeight;
    var targetFraction = productCount > 0 ? targetIndex / productCount : 0;
    var cgRect = cg.getBoundingClientRect();
    var cgTopAbsolute = cgRect.top + window.pageYOffset;
    var targetScrollY = cgTopAbsolute + targetFraction * totalScrollableDistance;

    var currentY = window.pageYOffset;
    var distance = Math.abs(targetScrollY - currentY);
    var duration = Math.max(800, Math.min(1600, distance * 0.8));

    animateScrollTo(targetScrollY, duration);
  }

  function positionTitle() {
    var titleEl = document.getElementById('cgTitle');
    if (!titleEl || !stage || !cg) return;
    var meta = slideMetadata[0];
    if (!meta) return;

    var stageRect = stage.getBoundingClientRect();
    var stageWidth = stageRect.width;
    var stageHeight = stageRect.height;
    if (stageWidth === 0 || stageHeight === 0) return;

    var ar = meta.aspectRatio;
    var maxCentreWidth;
    if (isMobilePortrait()) {
      maxCentreWidth = stageWidth * MOBILE_PORTRAIT_STAGE_WIDTH_FRACTION;
    } else if (isMobileLandscape()) {
      maxCentreWidth = stageWidth * MOBILE_LANDSCAPE_STAGE_WIDTH_FRACTION;
    } else {
      maxCentreWidth = Math.min(stageWidth * STAGE_WIDTH_FRACTION, STAGE_MAX_WIDTH);
    }

    var cw, ch;
    if (ar >= 1) {
      cw = maxCentreWidth;
      ch = cw / ar;
      if (ch > stageHeight) { ch = stageHeight; cw = ch * ar; }
    } else {
      ch = stageHeight;
      cw = ch * ar;
      if (cw > maxCentreWidth) { cw = maxCentreWidth; ch = cw / ar; }
    }

    var stageLeftFromCg = stageRect.left - cg.getBoundingClientRect().left;
    var imgLeftFromCg = stageLeftFromCg + (stageWidth - cw) / 2;
    var val = Math.max(8, Math.round(imgLeftFromCg)) + 'px';

    if (isMobile()) {
      cg.style.setProperty('--cg-title-left-m', val);
    } else {
      cg.style.setProperty('--cg-title-left', val);
    }
  }

  function alignTextForSlide(slideIndex) {
    var meta = slideMetadata[slideIndex];
    if (!meta) return;
    if (!stage || !cg) return;

    if (isMobile()) {
      return;
    }

    var stageRect = stage.getBoundingClientRect();
    var stageWidth = stageRect.width;
    var stageHeight = stageRect.height;
    if (stageWidth === 0 || stageHeight === 0) return;

    var maxCentreWidth = Math.min(stageWidth * STAGE_WIDTH_FRACTION, STAGE_MAX_WIDTH);
    var ar = meta.aspectRatio;

    var slideContainerWidth, slideContainerHeight;
    if (ar >= 1) {
      slideContainerWidth = maxCentreWidth;
      slideContainerHeight = slideContainerWidth / ar;
      if (slideContainerHeight > stageHeight) {
        slideContainerHeight = stageHeight;
        slideContainerWidth = slideContainerHeight * ar;
      }
    } else {
      slideContainerHeight = stageHeight;
      slideContainerWidth = slideContainerHeight * ar;
      if (slideContainerWidth > maxCentreWidth) {
        slideContainerWidth = maxCentreWidth;
        slideContainerHeight = slideContainerWidth / ar;
      }
    }

    var containerAR = slideContainerWidth / slideContainerHeight;
    var renderedImgWidth, renderedImgHeight;
    if (ar > containerAR) {
      renderedImgWidth = slideContainerWidth;
      renderedImgHeight = slideContainerWidth / ar;
    } else if (ar < containerAR) {
      renderedImgHeight = slideContainerHeight;
      renderedImgWidth = slideContainerHeight * ar;
    } else {
      renderedImgWidth = slideContainerWidth;
      renderedImgHeight = slideContainerHeight;
    }

    var stageLeftFromCg = stageRect.left - cg.getBoundingClientRect().left;
    var slideLeftFromStage = (stageWidth - slideContainerWidth) / 2;
    var imgLeftFromSlide = (slideContainerWidth - renderedImgWidth) / 2;
    var imgLeftFromCg = stageLeftFromCg + slideLeftFromStage + imgLeftFromSlide;
    var imgRightFromCg = cg.offsetWidth - imgLeftFromCg - renderedImgWidth;

    cg.style.setProperty('--cg-text-anchor-left', imgLeftFromCg + 'px');
    cg.style.setProperty('--cg-text-anchor-right', imgRightFromCg + 'px');

    /* Anchor the text a fixed distance below the painting's bottom edge,
       like a placard in a live gallery, regardless of frame proportions */
    var bottomGap = Math.max(0, (stageHeight - slideContainerHeight) / 2);
    cg.style.setProperty('--cg-text-pull', bottomGap + 'px');
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();